import { Scene } from '@babylonjs/core'
import { checkDeviceTier, DeviceInfo } from "@modules/babylonjs/src/DeviceInfo";
import { Layers } from '@modules/core';
import { DoubleTouchPinchZoom, getDeviceInfo, MouseWheelZoom, ProfileDebugInfo } from '@modules/standard';
import { useEffect, useState } from "react";

import { TourDanjiCameraAPI } from '../camera/TourDanjiCameraAPI';
import { INTERIOR_FOV_DESKTOP, INTERIOR_FOV_MOBILE } from '../Constants';
import { TourDanjiOptions } from "../TourDanjiModule";
import FPS from "../utiles/FPS";

export type DebugProps = {
  danjiId?: string | undefined;
  appVersion?: string | undefined;
  deviceInfo?: DeviceInfo | undefined;
  danjiOptions?: TourDanjiOptions | undefined;
  loadingTime?: number | undefined;
  scene?: Scene | undefined;
  profileDebugInfo?: ProfileDebugInfo | undefined;
  tier?: string | undefined;
  getEntityInfo?: (() => {
    totalEntities: number;
    componentStats: Array<{
      name: string;
      count: number;
      percentage: string;
    }>;
  } | null) | undefined;
};

export function DebugPanel(props: DebugProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [fps, setFps] = useState<number | undefined>();
  const { loadingTime, deviceInfo, profileDebugInfo, scene, getEntityInfo } = props;

  const engine = scene?.getEngine();
  const gl = engine?.getRenderingCanvas()?.getContext('webgl2');

  const debugInfo = [
    { label: 'tier', value: deviceInfo ? checkDeviceTier(deviceInfo?.modelNumber, deviceInfo?.deviceMemory) : null },
    { label: 'load time', value: loadingTime ? `${loadingTime.toFixed(2)}s` : null },    
    { label: 'ram', value: deviceInfo?.deviceMemory },
    { label: 'platform', value: deviceInfo?.platform },
    { label: 'gpu', value: deviceInfo?.gpuModel },
    { label: 'gl ver.', value: deviceInfo?.openglVersion },
    { label: 'vertices', value: profileDebugInfo?.vertexCount },
    { label: 'materials', value: profileDebugInfo?.materialCount },
    { label: 'triangles', value: profileDebugInfo?.triangleCount },
    { label: 'powPref.', value: gl?.getContextAttributes()?.powerPreference ? gl?.getContextAttributes()?.powerPreference : null },
    { label: 'total entities', value: getEntityInfo?.()?.totalEntities },
  ];

  const entityInfo = getEntityInfo?.();
  
  const zoom = getDeviceInfo().isMobileOrTablet ? DoubleTouchPinchZoom : MouseWheelZoom;
  const [sensibility, setSensibility] = useState<number | null>(null);
  const [radiusDampening, setRadiusDampening] = useState<number | null>(null);
  const [interiorFov, setInteriorFov] = useState<number | null>(null);

  useEffect(() => {
    setSensibility(zoom.option.sensibility);
    setRadiusDampening(zoom.option.radiusDampening);
    setInteriorFov(getDeviceInfo().isMobileOrTablet ? INTERIOR_FOV_MOBILE : INTERIOR_FOV_DESKTOP);
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (scene && FPS.measure() === -1) {
        FPS.reset(scene);
      } else {
        setFps(FPS.measure());
      }
    }, 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (sensibility !== null) {
      zoom.option.sensibility = sensibility;
    }
  }, [sensibility]);

  useEffect(() => {
    if (radiusDampening !== null) {
      zoom.option.radiusDampening = radiusDampening;
    }
  }, [radiusDampening]);

  useEffect(() => {
    if (interiorFov !== null) {
      TourDanjiCameraAPI._interiorFov = interiorFov;
    }
  }, [interiorFov]);
  
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 5,
        zIndex: Layers.BABYLON_DEBUG,
        pointerEvents: 'auto',
      }}>
      {isExpanded ? (
        <div style={{
          whiteSpace: 'pre-line',
          textShadow: `0px 0px 2px #000`,
          color: '#00ff00',
          fontSize: '9px',
          textAlign: 'left',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          padding: '10px',
          borderRadius: '5px',
        }}>
          <div style={{
            cursor: 'pointer',
            marginBottom: '10px',
            fontSize: '12px',
            textAlign: 'right'
          }} onClick={() => setIsExpanded(false)}>
            ✕
          </div>
          <div className="debug-section" style={{
              marginBottom: '15px'
            }}>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '14px' }}>Zoom Parameters</h3>
              <div className="debug-control" style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={{ width: '120px', fontSize: '12px' }}>Sensibility:</label>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  step="1"
                  value={sensibility ?? ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setSensibility(null);
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                        setSensibility(numValue);
                      }
                    }
                  }}
                  style={{ 
                    width: '80px',
                    padding: '4px',
                    fontSize: '12px'
                  }}
                />
              </div>
              <div className="debug-control" style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={{ width: '120px', fontSize: '12px' }}>Distance Dampening:</label>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="100"
                  step="1"
                  value={radiusDampening ?? ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setRadiusDampening(null);
                    } else {
                      const numValue = parseInt(value);
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                        setRadiusDampening(numValue);
                      }
                    }
                  }}
                  style={{ 
                    width: '80px',
                    padding: '4px',
                    fontSize: '12px'
                  }}
                />
              </div>
              <div className="debug-control" style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '8px'
              }}>
                <label style={{ width: '120px', fontSize: '12px' }}>Interior FOV:</label>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  max="2"
                  step="0.01"
                  value={interiorFov ?? ''}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.currentTarget.blur();
                    }
                  }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setInteriorFov(null);
                    } else {
                      const numValue = parseFloat(value);
                      if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                        setInteriorFov(numValue);
                      }
                    }
                  }}
                  style={{ 
                    width: '80px',
                    padding: '4px',
                    fontSize: '12px'
                  }}
                />
              </div>
            </div>
          <span>
            {fps && `fps:${fps}\n`}
          </span>
          {debugInfo.map((info, index) => (
            info.value && <span key={index}>{`${info.label}: ${info.value}\n`}</span>
          ))}
          {entityInfo?.componentStats && entityInfo.componentStats.length > 0 && (
            <>
              <div style={{ marginTop: '10px', fontSize: '12px' }}>Component Stats:</div>
              {entityInfo.componentStats.map((stat, index) => (
                <span key={index}>{`${stat.name}: ${stat.count} (${stat.percentage}%)\n`}</span>
              ))}
            </>
          )}
        </div>
      ) : (
        <div
          onClick={() => setIsExpanded(true)}
          style={{
            cursor: 'pointer',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: '#00ff00',
            padding: '5px 10px',
            borderRadius: '5px',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}>
          <span>Debug</span>
          {fps && <span>{`(${fps}fps)`}</span>}
        </div>
      )}
    </div>
  );
}
